import { type ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUnit } from 'effector-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $recipientListStore, $recipientRecentStore } from '@lib/store';
import { cn, PaletteUtils } from '@shared/utils';
import { type RecipientResponse, SortType, TransferRecipientTypeEnum } from '@shared/api';
import { SelectRecipient } from './selectRecipient';
import { RecentRecipient } from './recentRecipient';
import { RecipientDetails } from './recipientDetails';
import { View } from '@widgets';
import { Radio, Text } from '@shared/widgets';

const RecipientType = {
  Existing: 'Existing Recipient',
  New: 'New Recipient',
} as const;
type RecipientType = (typeof RecipientType)[keyof typeof RecipientType];

const RECIPIENT_TYPE_OPTIONS = Object.entries(RecipientType).map(([, text]) => ({ text, value: text }));

interface RecipientSelectBlockProps {
  className?: string;
  type: TransferRecipientTypeEnum;
  isInternalAccount?: boolean;
  value?: RecipientResponse | null;
  onChange: (recipient?: RecipientResponse | null) => void;
  error?: ReactNode;
}

export const RecipientSelectBlock = (props: RecipientSelectBlockProps) => {
  const [searchParams] = useSearchParams();
  const recipientId = searchParams.get('recipientId');

  const recipientListStore = useUnit($recipientListStore.store);
  const recipientRecentStore = useUnit($recipientRecentStore.store);

  const [recipientType, setRecipientType] = useState<RecipientType>(RecipientType.Existing);
  const [searchValue, setSearchValue] = useState<string>('');

  const { setValue } = useFormContext();
  const { t } = useTranslation('payments');

  useEffect(() => {
    $recipientRecentStore.events.getRecent({ Type: props.type, isInternalAccount: !!props.isInternalAccount });
    return () => $recipientRecentStore.events.clear();
  }, [props.isInternalAccount, props.type]);

  useEffect(() => {
    $recipientListStore.events.getRecipients({
      SearchQuery: searchValue,
      isInternalAccount: !!props.isInternalAccount,
      Type: props.type,
      SortType: SortType.Asc,
      ReturnTotalCount: true,
      Count: 999,
    });
    return () => $recipientListStore.events.clear();
  }, [searchValue, props.isInternalAccount, props.type]);

  useEffect(() => {
    if (!recipientId) return;
    const recipient = recipientListStore.data.data.find((recipient) => recipient.id === recipientId);
    if (recipient) props.onChange(recipient);
  }, [recipientId, recipientListStore.data.data]);

  return (
    <div className={cn('flex flex-col gap-6 bg-white dark:bg-soft-black', PaletteUtils.isInsigniaPalette && 'p-6', props.className)}>
      <Radio
        orientation='horizontal'
        options={RECIPIENT_TYPE_OPTIONS}
        value={recipientType}
        onChange={(value) => {
          setRecipientType(value);
          switch (value) {
            case RecipientType.Existing:
              props.onChange(undefined);
              setValue('currency', undefined);
              break;
            case RecipientType.New:
              props.onChange(null);
              break;
            default: {
              const never: never = value;
              return never;
            }
          }
        }}
      />
      {recipientType === RecipientType.Existing && (
        <>
          <SelectRecipient
            label={t('recipient.placeholders.transferTo')}
            loading={recipientListStore.loading}
            items={recipientListStore.data.data}
            value={props.value}
            error={props.error}
            onChange={props.onChange}
            searchValue={searchValue}
            onSearch={setSearchValue}
          />
          {!props.value && (
            <View
              loading={recipientRecentStore.loading}
              error={recipientRecentStore.error}
              onRefresh={() => $recipientRecentStore.events.getRecent({ Type: props.type, isInternalAccount: !!props.isInternalAccount })}
            >
              {recipientRecentStore.data.length > 0 && (
                <div className='flex flex-col gap-3'>
                  <Text className={cn(PaletteUtils.isVcfcPalette && 'dark:text-white/50')}>{t('recipient.title')}</Text>
                  <div className='flex gap-2 overflow-x-auto'>
                    {recipientRecentStore.data.map((recipient) => (
                      <RecentRecipient key={recipient.id} recipient={recipient} onClick={() => props.onChange(recipient)} />
                    ))}
                  </div>
                </div>
              )}
            </View>
          )}
          {props.value && <RecipientDetails recipient={props.value} />}
        </>
      )}
    </div>
  );
};
